import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Bookmark as BookmarkIcon, ExternalLink, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";

function hostnameOf(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

export default function BookmarksPanel() {
  const confirm = useConfirm();
  const { t } = useT();
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/bookmarks");
      setBookmarks(data);
    } catch {
      toast.error(t("bmLoadFailed"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!name.trim() || !url.trim()) {
      toast.error(t("bmRequired"));
      return;
    }
    setAdding(true);
    try {
      const { data } = await api.post("/bookmarks", { name: name.trim(), url: url.trim() });
      setBookmarks((prev) => [...prev, data]);
      setName("");
      setUrl("");
      toast.success(t("bmCreated"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("bmCreateFailed"));
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirm(t("bmConfirmDelete")))) return;
    try {
      await api.delete(`/bookmarks/${id}`);
      setBookmarks((prev) => prev.filter((b) => b.id !== id));
      toast.success(t("bmDeleted"));
    } catch {
      toast.error(t("bmDeleteFailed"));
    }
  };

  return (
    <section className="mb-14" data-testid="bookmarks-panel">
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <p className="ed-eyebrow flex items-center gap-2">
            <BookmarkIcon size={12} strokeWidth={1.5} /> {t("bmTitle")}
          </p>
          <p className="font-serif text-2xl mt-1 italic text-neutral-800">{t("bmSubtitle")}</p>
        </div>
      </div>

      <form onSubmit={handleAdd} className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-6">
        <div className="md:col-span-4">
          <label className="ed-label">{t("bmName")}</label>
          <input
            data-testid="bookmark-name-input"
            type="text"
            placeholder={t("bmNamePh")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="ed-input"
          />
        </div>
        <div className="md:col-span-6">
          <label className="ed-label">{t("bmUrl")}</label>
          <input
            data-testid="bookmark-url-input"
            type="text"
            inputMode="url"
            placeholder={t("bmUrlPh")}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="ed-input"
          />
        </div>
        <div className="md:col-span-2 flex items-end">
          <button
            type="submit"
            data-testid="bookmark-add-button"
            disabled={adding}
            className="ed-btn w-full"
          >
            <Plus size={14} strokeWidth={1.5} />
            {adding ? t("bmAdding") : t("bmAdd")}
          </button>
        </div>
      </form>

      {loading ? (
        <div className="ed-card p-8 text-center">
          <p className="ed-eyebrow">{t("loading")}</p>
        </div>
      ) : bookmarks.length === 0 ? (
        <div className="ed-card p-8 text-center" data-testid="bookmarks-empty">
          <p className="text-sm text-neutral-500 italic font-serif">{t("bmEmpty")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-neutral-200">
          {bookmarks.map((bm, idx) => (
            <div
              key={bm.id}
              data-testid={`bookmark-card-${idx}`}
              className="bg-white p-5 flex items-start justify-between gap-3 group hover:bg-[#FBFAF7] transition-colors"
            >
              <a
                href={bm.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`bookmark-link-${idx}`}
                title={t("bmOpenNew")}
                className="flex-1 min-w-0"
              >
                <p className="font-serif text-xl leading-tight text-neutral-900 group-hover:text-neutral-700 transition-colors">
                  {bm.name}
                </p>
                <p className="font-mono text-xs text-neutral-500 mt-1 truncate flex items-center gap-1">
                  <ExternalLink size={11} strokeWidth={1.5} />
                  {hostnameOf(bm.url)}
                </p>
              </a>
              <button
                data-testid={`bookmark-delete-${idx}`}
                onClick={() => handleDelete(bm.id)}
                className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 border border-neutral-300 hover:border-rose-700 hover:bg-rose-700 hover:text-white shrink-0"
                title={t("bmRemove")}
              >
                <Trash2 size={12} strokeWidth={1.5} />
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
